import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiError, downloadFile } from "@/lib/api";

export const TemplateDownload = ({ units = [], plantNames = {}, myPlant, version, firstFill = [] }) => {
  const [unit, setUnit] = useState(myPlant || units[0] || "");
  const [busy, setBusy] = useState(false);
  const all = !myPlant && units.length > 1;

  useEffect(() => {
    setUnit((current) => current || myPlant || (units.length > 1 ? "ALL" : units[0]) || "");
  }, [myPlant, units]);

  const pull = async () => {
    setBusy(true);
    try {
      const label = unit === "ALL" ? "SAIL" : unit;
      const d = new Date();
      const stamp = [d.getDate(), d.getMonth() + 1]
        .map((n) => String(n).padStart(2, "0"))
        .concat(d.getFullYear())
        .join(".");
      await downloadFile(
        "/registers/template",
        { plant: unit === "ALL" ? "" : unit },
        `${label} Dx Project Status as on ${stamp}.xlsx`,
      );
      toast.success(
        unit === "ALL"
          ? "Workbook ready — every unit is on its own sheet."
          : `${unit} workbook ready, pre-filled with what the portal holds today.`,
      );
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-8 border border-border bg-card p-5 sm:flex sm:items-center sm:justify-between sm:gap-6"
      data-testid="template-download"
    >
      <div className="max-w-2xl">
        <div className="label-caps">Bulk update · workbook {version || "v2"}</div>
        <h2 className="font-display mt-1 text-base font-bold sm:text-lg">
          Take out the pre-filled workbook, correct it, bring it back
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          Every use case the portal holds comes down already filled in — status, sub-stage, the six
          milestone dates, provider and cost. Correct the yellow cells, add a new use case at the
          bottom with the code left blank, and drop the file back here. A use case name or its
          project type can be corrected too — the portal records the correction; only the Use Case
          Code and the Unit stay its own. To take a use case off the list, set its status to Dropped
          with a reason and a date.
        </p>
        {firstFill.length > 0 && (
          <p
            className="mt-3 border border-border bg-muted p-3 text-xs"
            data-testid="first-fill-note"
          >
            <b>First fill</b> — {firstFill.join(", ")} {firstFill.length === 1 ? "has" : "have"} not
            brought a workbook in yet, so <b>this upload replaces what the portal holds</b> for{" "}
            {firstFill.length === 1 ? "that unit" : "those units"}: every figure comes from the
            workbook, a cell left blank clears what was held, and every row is stamped with today's
            date, who entered it and who approved it. Afterwards the same thing can be asked for
            with the "complete register" tick on the upload panel.
          </p>
        )}
      </div>
      <div className="mt-4 flex shrink-0 items-center gap-3 sm:mt-0">
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger className="h-11 w-[170px] rounded-none" data-testid="template-unit">
            <SelectValue placeholder="Unit" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            {all && <SelectItem value="ALL">All units, one file</SelectItem>}
            {units.map((u) => (
              <SelectItem key={u} value={u}>
                {u} — {plantNames[u] || u}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
          onClick={pull}
          disabled={busy || !unit}
          data-testid="download-template"
        >
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Download
        </Button>
      </div>
    </div>
  );
};
